import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import { Icon } from '../icons/Icon';
import { haptic, Tooltip } from '../primitives';
import { curves, seconds } from '../theme/motion';
import { TABS, tabIndex, type TabId } from './destinations';
import { useNavigationFocusHandOff } from './focus';
import styles from './SlidingNavBar.module.css';

export interface SlidingNavBarProps {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
}

const FOCUS_RING_MAX_WIDTH = 80;
const FOCUS_RING_MIN_MARGIN = 8;

function snapToPhysicalPixel(value: number): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return Math.round(value * dpr) / dpr;
}

function isModifiedClick(event: MouseEvent): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/**
 * Bottom navigation: five icon-only cells (outline ↔ filled cross-fade) and a
 * 4 px active dot that slides 300 ms easeOutCubic. No highlight behind the
 * icons, on press or on change: the dot and the icon colour mark the tab.
 */
export function SlidingNavBar({ activeTab, onSelect }: SlidingNavBarProps) {
  const index = tabIndex(activeTab);
  const innerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  useNavigationFocusHandOff(navRef);
  const reduceMotion = useReducedMotion();
  const animatedIndex = useMotionValue(index);
  const segment = useMotionValue(0);
  const [segmentWidth, setSegmentWidth] = useState(0);
  const previousIndex = useRef(index);
  const left = useTransform(() => snapToPhysicalPixel(animatedIndex.get() * segment.get()));

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth / TABS.length;
      segment.set(width);
      setSegmentWidth(width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [segment]);

  useEffect(() => {
    if (previousIndex.current === index) return;
    previousIndex.current = index;
    const controls = animate(animatedIndex, index, {
      duration: reduceMotion ? 0 : seconds.navPill,
      ease: curves.easeOutCubic,
    });
    return () => controls.stop();
  }, [index, animatedIndex, reduceMotion]);

  const focusRingWidth =
    segmentWidth > FOCUS_RING_MAX_WIDTH ? FOCUS_RING_MAX_WIDTH : Math.max(0, segmentWidth - FOCUS_RING_MIN_MARGIN * 2);

  return (
    <nav ref={navRef} className={styles.bar} aria-label="Main">
      <div ref={innerRef} className={styles.inner}>
        <div className={styles.items} style={focusRingWidth > 0 ? ({ '--nav-focus-width': `${focusRingWidth}px` } as CSSProperties) : undefined}>
          {TABS.map((tab, i) => {
            const selected = i === index;
            return (
              <Tooltip key={tab.id} message={tab.label}>
                <a
                  href={tab.path}
                  className={styles.cell}
                  aria-label={tab.label}
                  aria-current={selected ? 'page' : undefined}
                  onClick={(event) => {
                    if (isModifiedClick(event)) return;
                    event.preventDefault();
                    // A selection tick on a tab change (polish pass); a re-tap scrolls up silently.
                    if (!selected) haptic('selection');
                    onSelect(tab.id);
                  }}
                >
                  <span className={styles.icon}>
                    <Icon
                      name={tab.icon}
                      size={22}
                      color="var(--color-icon-muted)"
                      className={[styles.glyph, selected ? '' : styles.glyphOn].filter(Boolean).join(' ')}
                    />
                    <Icon
                      name={tab.selectedIcon}
                      size={22}
                      color="var(--color-primary)"
                      className={[styles.glyph, selected ? styles.glyphOn : ''].filter(Boolean).join(' ')}
                    />
                  </span>
                </a>
              </Tooltip>
            );
          })}
        </div>
        <motion.div className={styles.dotTrack} style={{ x: left, width: segment }} aria-hidden>
          <div className={styles.dot} />
        </motion.div>
      </div>
    </nav>
  );
}
